import { FC, memo, useState } from 'react';
// import { useTranslation } from 'react-i18next';
import * as Styled from './EventInfo.styles';

// import { FC, memo, useState } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';

import { Button } from 'features/common/components/Button';
/*
Needed if we comment out in person tickets too
import { useAppSelector } from 'store/hooks';
import { RootState } from 'store';
*/
import { DuplicateEvent } from './services';
import { EventInfoProps } from './types';
import { IdParamsType } from 'utils/types';
import { ROUTES } from 'features/routes';
import { SECONDARY } from 'features/common/components/Button/constants';
import { ZERO_TIME } from 'utils/constants';
import { useMutation } from '@tanstack/react-query';
import { useResponsiveScreen } from 'hooks/useWindowSize';
import { useToastNotification } from 'hooks/useToastNotification';
import { useTranslation } from 'react-i18next';
import { LotteryCodesModal } from './LotteryCodesModal';
import useGetEvent from 'features/common/hooks/useGetEvent';
import { useAppSelector } from 'store/hooks';
import { RootState } from 'store';

const EventInfo: FC<EventInfoProps> = ({
	address,
	date,
	endTime,
	eventTitle,
	onSaleDate,
	startTime,
	hasPassword,
}: EventInfoProps) => {
	const { t } = useTranslation();
	const { push } = useHistory();
	const { event } = useGetEvent();
	const isLargeScreen = useResponsiveScreen();
	const { id: eventId }: IdParamsType = useParams();
	const { showNotification, TYPES } = useToastNotification();
	const { ticketsAvailable } = useAppSelector(({ boxOffice }: RootState) => boxOffice);

	const [scannerAvailable] = useState(true);
	const [generateQRCodesModalOpen, setGenerateQRCodesModalOpen] = useState(false);

	const zeroTime = onSaleDate === ZERO_TIME;
	const hasLotteryTickets = event?.ticketTypes?.some((tt) => tt.isLottery);

	const { mutate: mutateDuplicateEvent } = useMutation(DuplicateEvent, {
		onSuccess: ({ data }) => {
			push(generatePath(ROUTES.DASHBOARD_PATH, { id: data.ID }));
		},
		onError: () => {
			showNotification({
				type: TYPES.ERROR,
				message: t('dashboard-duplicateEvent-error'),
			});
		},
	});

	const onClickEdit = () => push(ROUTES.EDIT_EVENT_PATH.replace(':id', eventId));

	const duplicateEvent = () => {
		mutateDuplicateEvent({ eventId });
	};

	const generateQRCodesCSV = () => {
		setGenerateQRCodesModalOpen(true);
	};

	return (
		<>
			<Styled.Container>
				<Styled.InnerContainer>
					<Styled.InfoContainer>
						{!isLargeScreen && (
							<Button text={'Edit Details'} category={SECONDARY} onClick={onClickEdit} type="button" />
						)}
						<Styled.Title>
							{eventTitle}
							{hasPassword && <Styled.LockIcon iconName="lock" />}
							{isLargeScreen && <Styled.Icon iconName="pencil" onClick={onClickEdit} data-cy="edit-event" />}
						</Styled.Title>
						<Styled.Text>{date}</Styled.Text>
						<Styled.Text>
							{startTime} - {endTime}
						</Styled.Text>
						<Styled.Text>{address}</Styled.Text>
						{!zeroTime && <Styled.Text>{`${t('generic-onSale')} ${onSaleDate}`}</Styled.Text>}
					</Styled.InfoContainer>

					<Styled.OutlinedButtonContainer>
						{ticketsAvailable && (
							<Styled.OutlinedButton
								text={t('dashboard-inPersonTickets')}
								type="button"
								category="outlined"
								onClick={() => push(generatePath(ROUTES.BOX_OFFICE_LEGACY, { id: eventId }))}
							/>
						)}

						{scannerAvailable && (
							<Styled.OutlinedButton
								text={t('dashboard-scanner')}
								type="button"
								category="outlined"
								onClick={() => push(generatePath(ROUTES.SCANNER, { id: eventId }))}
							/>
						)}

						<Styled.OutlinedButton
							text={t('dashboard-duplicateEvent')}
							type="button"
							category="outlined"
							onClick={duplicateEvent}
						/>

						<Styled.OutlinedLinkButton
							href={`${process.env.REACT_APP_API_BASE}/public/ticketing/events/${eventId}/qr`}
							download
						>
							{t('dashboard-downloadQR')}
						</Styled.OutlinedLinkButton>

						{hasLotteryTickets && (
							<Styled.OutlinedButton
								text={t('dashboard-lotteryCodes-button')}
								type="button"
								category="outlined"
								onClick={generateQRCodesCSV}
							/>
						)}
					</Styled.OutlinedButtonContainer>
				</Styled.InnerContainer>
			</Styled.Container>

			{generateQRCodesModalOpen && (
				<LotteryCodesModal
					eventId={eventId}
					ticketTypes={event?.ticketTypes ?? []}
					closeModal={() => setGenerateQRCodesModalOpen(false)}
				/>
			)}
		</>
	);
};

const memorizedEventInfo: FC<EventInfoProps> = memo(EventInfo);

export { memorizedEventInfo as EventInfo };
